import * as THREE from 'three';
import { PhysicsBody } from '../types';

/**
 * Explosion effect with physics-based debris.
 * Optimized for performance.
 */
export class Explosion {
  public readonly id: string;
  public readonly mesh: THREE.Group;
  public debrisBodies: PhysicsBody[] = [];

  private alive: boolean = true;
  private age: number = 0;
  private readonly lifetime: number = 6;

  // Visual elements - simplified
  private flash: THREE.Mesh;
  private debris: THREE.Group[] = [];
  private particlePositions!: Float32Array;
  private particleVelocities: THREE.Vector3[] = [];
  private particles: THREE.Points;

  constructor(position: THREE.Vector3, size: number = 5, inheritedVelocity: THREE.Vector3 = new THREE.Vector3()) {
    this.id = `explosion_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    this.mesh = new THREE.Group();
    this.mesh.name = this.id;
    this.mesh.position.copy(position);

    // Create initial flash - simple sphere
    this.flash = this.createFlash(size);
    this.mesh.add(this.flash);

    // Create particle system
    this.particles = this.createParticles(size);
    this.mesh.add(this.particles);

    // Create physics debris - fewer pieces
    this.createDebris(position, size, inheritedVelocity);
  }

  private createFlash(size: number): THREE.Mesh {
    const geo = new THREE.SphereGeometry(size * 1.5, 8, 6);
    const mat = new THREE.MeshBasicMaterial({
      color: 0xffaa00,
      transparent: true,
      opacity: 1,
    });
    return new THREE.Mesh(geo, mat);
  }

  private createParticles(size: number): THREE.Points {
    const particleCount = 50; // Reduced count
    this.particlePositions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount; i++) {
      this.particlePositions[i * 3] = 0;
      this.particlePositions[i * 3 + 1] = 0;
      this.particlePositions[i * 3 + 2] = 0;

      const vel = new THREE.Vector3(
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.5) * 2
      ).normalize().multiplyScalar(size * (1 + Math.random() * 2));
      this.particleVelocities.push(vel);

      // Orange/red colors
      colors[i * 3] = 1;
      colors[i * 3 + 1] = 0.3 + Math.random() * 0.4;
      colors[i * 3 + 2] = 0;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.particlePositions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const mat = new THREE.PointsMaterial({
      size: 3,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      sizeAttenuation: true,
    });

    return new THREE.Points(geo, mat);
  }

  private createDebris(position: THREE.Vector3, size: number, inheritedVelocity: THREE.Vector3): void {
    const debrisCount = 5; // Reduced count
    const debrisMat = new THREE.MeshBasicMaterial({ color: 0x444444 });

    for (let i = 0; i < debrisCount; i++) {
      const debrisGroup = new THREE.Group();

      const debrisSize = size * 0.15 * (0.5 + Math.random() * 0.5);
      const geo = new THREE.BoxGeometry(debrisSize, debrisSize * 0.6, debrisSize * 0.8);
      const mesh = new THREE.Mesh(geo, debrisMat.clone());
      debrisGroup.add(mesh);

      const offset = new THREE.Vector3(
        (Math.random() - 0.5) * size,
        (Math.random() - 0.5) * size,
        (Math.random() - 0.5) * size
      );
      debrisGroup.position.copy(position).add(offset);

      const outwardDir = offset.clone().normalize();
      const speed = 15 + Math.random() * 25;
      const velocity = outwardDir.multiplyScalar(speed).add(inheritedVelocity.clone().multiplyScalar(0.3));

      debrisGroup.userData.rotationSpeed = new THREE.Vector3(
        (Math.random() - 0.5) * 4,
        (Math.random() - 0.5) * 4,
        (Math.random() - 0.5) * 4
      );

      this.debris.push(debrisGroup);

      const physicsBody: PhysicsBody = {
        id: `${this.id}_debris_${i}`,
        position: debrisGroup.position.clone(),
        velocity: velocity,
        acceleration: new THREE.Vector3(),
        mass: 1,
        radius: debrisSize,
        isStatic: false,
        object3D: debrisGroup,
      };

      this.debrisBodies.push(physicsBody);
    }
  }

  public update(deltaTime: number): void {
    if (!this.alive) return;

    this.age += deltaTime;

    if (this.age >= this.lifetime) {
      this.alive = false;
      return;
    }

    const progress = this.age / this.lifetime;

    // Flash fades quickly
    if (this.age < 0.2) {
      const flashProgress = this.age / 0.2;
      this.flash.scale.setScalar(1 + flashProgress * 3);
      (this.flash.material as THREE.MeshBasicMaterial).opacity = 1 - flashProgress;
    } else {
      this.flash.visible = false;
    }

    // Update particles
    const positions = this.particles.geometry.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < this.particleVelocities.length; i++) {
      const vel = this.particleVelocities[i];
      this.particlePositions[i * 3] += vel.x * deltaTime;
      this.particlePositions[i * 3 + 1] += vel.y * deltaTime;
      this.particlePositions[i * 3 + 2] += vel.z * deltaTime;
      vel.multiplyScalar(0.97);
    }
    positions.needsUpdate = true;
    (this.particles.material as THREE.PointsMaterial).opacity = 1 - progress;

    // Update debris rotation
    this.debris.forEach((debris) => {
      const rotSpeed = debris.userData.rotationSpeed as THREE.Vector3;
      debris.rotation.x += rotSpeed.x * deltaTime;
      debris.rotation.y += rotSpeed.y * deltaTime;
      debris.rotation.z += rotSpeed.z * deltaTime;
    });
  }

  public isAlive(): boolean {
    return this.alive;
  }

  public getDebrisMeshes(): THREE.Group[] {
    return this.debris;
  }

  public dispose(): void {
    this.flash.geometry.dispose();
    (this.flash.material as THREE.Material).dispose();

    this.particles.geometry.dispose();
    (this.particles.material as THREE.Material).dispose();

    this.debris.forEach((debris) => {
      debris.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (child.material instanceof THREE.Material) {
            child.material.dispose();
          }
        }
      });
    });
  }
}
